import { createContext, type PropsWithChildren, useContext, type ReactNode } from "react";

import {
  Dialog as ReactAriaDialog,
  type DialogProps as ReactAriaDialogProps,
  Heading,
  ModalOverlay,
  type ModalOverlayProps,
  type DialogRenderProps,
} from "react-aria-components";

import { Button } from "#components/Button";
import { ClearIcon } from "#icons";
import { cn, customTv } from "#utils";

import { Modal, type ModalSize } from "./Modal";

const headerStyles = customTv({
  base: "flex items-center justify-between p-6 pb-0",
});

const contentStyles = customTv({
  base: "p-6",
});

const footerStyles = customTv({
  base: "flex justify-end gap-x-4 p-6 pt-0",
});

export interface DialogProps
  extends
    ReactAriaDialogProps,
    Pick<
      ModalOverlayProps,
      | "isDismissable"
      | "isKeyboardDismissDisabled"
      | "isOpen"
      | "onOpenChange"
      | "shouldCloseOnInteractOutside"
    > {
  children?: ReactNode;
  size?: ModalSize;
  className?: string;
  /** Additional className applied to the ModalOverlay (backdrop) element */
  overlayClassName?: string;
}

export interface InnerDialogProps {
  children?: ReactNode | ((opts: DialogRenderProps) => ReactNode);
  className?: string;
}

const DialogContext = createContext<{ close: () => void } | null>(null);

function useDialog() {
  const ctx = useContext(DialogContext);
  if (!ctx) {
    throw new Error("Dialog subcomponent must be used inside <Dialog>");
  }
  return ctx;
}

/**
 * Dialog
 *
 * This file implements an accessible, composable Dialog (modal) component,
 * built upon react-aria-components and custom StackletUI v2 primitives.
 *
 * ## Main Components:
 * - `Dialog`: The root dialog/modal component. Handles open/close,
 *    focus trap, size, and accessibility.
 * - `DialogHeader`: Provides a title/header for the Dialog and (optionally) a close button.
 * - `DialogContent`: For main body content of the dialog.
 * - `DialogFooter`: For actions and buttons at the bottom of the dialog.
 * - `DialogTrigger`: Optional component for triggering (opening) the Dialog.
 *
 * ## Features:
 * - Sizes: Supports `sm`, `md`, `lg`, `xl` via the `size` prop.
 * - Controlled/uncontrolled modes: Controlled via `isOpen`/`onOpenChange`, or with DialogTrigger.
 * - Highly accessible: leverages react-aria and Heading for semantics.
 * - "onClosePress" hooks: Customize logic when the dialog is dismissed.
 * - Tailwind-based styling, with hooks for custom className overrides.
 * - Composable layout using context for child/footer access to dialog state or close fn.
 *
 * ## Usage Example:
 * ```tsx
 * <DialogTrigger>
 *   <Button>Open Dialog</Button>
 *   <Dialog size="md">
 *     <DialogHeader onClosePress={() => alert("Closed!")}>Dialog Title</DialogHeader>
 *     <DialogContent>
 *       <p>Dialog body goes here</p>
 *     </DialogContent>
 *     <DialogFooter>
 *       {({ close }) => (
 *         <Button onPress={close}>Close</Button>
 *       )}
 *     </DialogFooter>
 *   </Dialog>
 * </DialogTrigger>
 * ```
 *
 * ## Controlled Usage Example:
 * ```tsx
 * const [isOpen, setIsOpen] = useState(false);
 * // ...
 * <Button onPress={() => setIsOpen(true)}>Open</Button>
 * <Dialog isOpen={isOpen} onOpenChange={setIsOpen} size="sm">
 *   <DialogHeader>Controlled Dialog</DialogHeader>
 *   <DialogContent>...</DialogContent>
 *   <DialogFooter>
 *     {({ close }) => <Button onPress={close}>Close</Button>}
 *   </DialogFooter>
 * </Dialog>
 * ```
 */

export function Dialog({
  isOpen,
  onOpenChange,
  isDismissable,
  isKeyboardDismissDisabled,
  shouldCloseOnInteractOutside,
  overlayClassName,
  size,
  className,
  children,
  ...props
}: DialogProps) {
  return (
    <ModalOverlay
      className={cn(
        "fixed inset-0 z-50 flex items-center justify-center bg-neutral-900/20 p-4 backdrop-blur-sm",
        overlayClassName,
      )}
      isDismissable={isDismissable}
      isKeyboardDismissDisabled={isKeyboardDismissDisabled}
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      shouldCloseOnInteractOutside={shouldCloseOnInteractOutside}
    >
      <Modal className={className} size={size ?? "md"}>
        <ReactAriaDialog className="outline-none" role="dialog" {...props}>
          {({ close }) => (
            <DialogContext.Provider value={{ close }}>{children}</DialogContext.Provider>
          )}
        </ReactAriaDialog>
      </Modal>
    </ModalOverlay>
  );
}

export interface DialogHeaderProps extends PropsWithChildren {
  onClosePress?: () => void;
  className?: string;
  hideCloseButton?: boolean;
}

export function DialogHeader({
  children,
  className,
  onClosePress,
  hideCloseButton = false,
}: DialogHeaderProps) {
  const { close } = useDialog();

  return (
    <div className={headerStyles()}>
      <Heading
        className={cn("text-title-large font-semibold text-text-primary", className)}
        slot="title"
      >
        {children}
      </Heading>
      {!hideCloseButton ? (
        <Button
          aria-label="Close"
          className="text-text-tertiary transition-colors hover:text-text-primary"
          onPress={() => {
            try {
              onClosePress?.();
            } finally {
              close();
            }
          }}
          variant="plain"
        >
          <ClearIcon />
        </Button>
      ) : null}
    </div>
  );
}

export interface DialogContentProps extends InnerDialogProps {
  text?: string;
}

export function DialogContent({ text, children, className }: DialogContentProps) {
  const { close } = useDialog();

  return (
    <div className={cn(contentStyles(), className)}>
      {text ? <p className="text-text-secondary">{text}</p> : null}
      {typeof children === "function" ? children({ close }) : children}
    </div>
  );
}

export function DialogFooter({ children, className }: InnerDialogProps) {
  const { close } = useDialog();

  return (
    <div className={cn(footerStyles(), className)}>
      {typeof children === "function" ? children({ close }) : children}
    </div>
  );
}
